import { useInsertionEffect } from 'react';
import { CHASSIS, FONT, MOTION, SEMANTIC, TOUCH_TARGET } from '../theme/tokens.js';
import { DEFAULT_EASING } from './motion.js';

/**
 * The stylesheet for the primitives, and the one function that installs it.
 *
 * Why a string and not a `.css` file: `packages/ui` builds with `tsc --build`,
 * which copies nothing but TypeScript output into `dist/`. A `.css` import
 * would type-error, would not ship, and would tie this package to whichever
 * bundler the host happens to use. A string built FROM the tokens keeps one
 * source of truth for every value and works in any host, including a test that
 * never runs a bundler.
 *
 * Every color reads a CSS custom property first and falls back to the shipped
 * token: `var(--color-paper, #FAF9F5)`. So the app's theme layer (ADR-0003, Tailwind
 * v4 `@theme`) wins wherever it is present, and the primitives still render
 * correctly on their own before that layer exists.
 *
 * Team colors are NEVER hardcoded (rubric C6). They read `--team-primary` and
 * `--team-accent`, written at boot by `themeCssVariables()`, and fall back to
 * `--ink` so an unthemed host degrades to the chassis rather than to a
 * borrowed brand color.
 */

export type DialogSize = 'sm' | 'md' | 'lg';

/**
 * Dialog widths, in px.
 *
 * NOT in the design handoff, which specifies phone frames and browser columns
 * but never a modal width. Chosen against the drawn surfaces: `sm` matches the
 * 380 to 402px phone frames, `md` is the comfortable reading measure for the
 * confirm and prompt copy, `lg` is the widest panel in the browser layouts.
 * Flagged for design sign-off.
 */
export const DIALOG_MAX_WIDTH_PX: Readonly<Record<DialogSize, number>> = {
  sm: 400,
  md: 520,
  lg: 760,
};

/** Screen gutter on phone, from the handoff spacing scale. */
const GUTTER_PX = 20;

/** Radii, from the handoff: 8 buttons, 10 cards and modals, 20 sheet top. */
const RADIUS = { control: 8, surface: 10, sheetTop: 20, pill: 999 } as const;

const ink = `var(--color-ink, ${CHASSIS.ink})`;
const inkMuted = `var(--color-ink-muted, ${CHASSIS.inkMuted})`;
const inkFaint = `var(--color-ink-faint, ${CHASSIS.inkFaint})`;
const rule = `var(--color-rule, ${CHASSIS.rule})`;
const paper = `var(--color-paper, ${CHASSIS.paper})`;
const surfaceRaised = `var(--color-surface-raised, ${CHASSIS.surfaceRaised})`;
const surfaceSunken = `var(--color-surface-sunken, ${CHASSIS.surfaceSunken})`;
const success = `var(--color-success, ${SEMANTIC.success})`;
const danger = `var(--color-danger, ${SEMANTIC.danger})`;

/** Team slots. Fall back to the chassis, never to another team's color. */
const teamPrimary = `var(--color-team-primary, ${ink})`;
const teamAccent = `var(--color-team-accent, ${ink})`;
/** The AA-checked shade for accent TYPE, emitted by `themeCssVariables()`. */
/**
 * The AA-resolved accent, and the one to use for anything that has to be SEEN
 * rather than merely filled.
 *
 * The focus ring and the ghost button's border used the raw accent, which is
 * whatever hex a coach typed. Measured with this repo's own `contrastRatio`
 * against paper: the sample royal `#0033A0` gives 10.06:1, the sample orange
 * `#E2711D` scrapes 3.01:1, and `#F2D33C`, which is the handoff's own worked
 * example of a colour that fails AA as text, gives **1.41:1**. That is an
 * invisible focus ring and an edgeless button, on a colour the theme editor is
 * designed to accept and then warn about.
 *
 * BUILD_PLAN 9.6 asks for 3:1 on non-text contrast. The fill keeps the true
 * team colour, which is the whole point of `resolveOnFill`; it is the outline
 * that has to be legible.
 */
const teamAccentText = `var(--color-team-accent-text, ${teamAccent})`;

/** Ink at an alpha, the way the handoff states its shadows and its scrim. */
const inkAlpha = (percent: number): string =>
  `color-mix(in srgb, ${ink} ${String(percent)}%, transparent)`;

export const PRIMITIVE_CSS = `
@keyframes gs-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes gs-fade-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes gs-dialog-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}
@keyframes gs-dialog-out {
  from { opacity: 1; transform: none; }
  to { opacity: 0; transform: translateY(4px); }
}
@keyframes gs-sheet-in {
  from { opacity: 0; transform: translateY(100%); }
  to { opacity: 1; transform: none; }
}
@keyframes gs-sheet-out {
  from { opacity: 1; transform: none; }
  to { opacity: 0; transform: translateY(100%); }
}

.gs-ui {
  /*
   * Type set ON a solid team fill, as opposed to team-colored type sitting on
   * paper, which is --color-team-primary-text. Those are different questions
   * and neither answers the other: white reads on a saturated team scarlet and is
   * invisible on the handoff's own pale yellow example.
   *
   * resolveOnFill() picks ink or paper by contrast and the injector emits the
   * result, so this is a real token now rather than the hardcoded white it
   * was when the primitives were written.
   */
  --gs-on-fill: var(--color-team-primary-on-fill, ${CHASSIS.surfaceRaised});
  font-family: ${FONT.body};
  color: ${ink};
}

.gs-ui :focus-visible,
.gs-ui:focus-visible,
/*
 * The primitives themselves, wherever they are.
 *
 * .gs-ui is only on Dialog and Sheet surfaces, so scoping the ring to their
 * descendants meant a Button or an Input on an ordinary page had NO focus ring
 * at all: the browser default, which CLAUDE.md forbids by name. Every element
 * on the sign-in screen was in that state. Caught by the e2e focus test once it
 * was pointed at a route that actually has something focusable.
 */
.gs-button:focus-visible,
.gs-input:focus-visible {
  /*
   * Visible focus ring, never the browser default (BUILD_PLAN 9.6). The
   * handoff SUGGESTS 2px --team-accent with a 2px --paper offset, matching the
   * season spine current-week ring, and flags it as needing design sign-off.
   * Built as a double box-shadow rather than outline-offset so the gap is
   * actually paper rather than whatever sits behind the element. The
   * transparent outline keeps a ring in forced-colors mode, where box-shadow
   * is dropped.
   */
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow:
    0 0 0 2px ${paper},
    0 0 0 4px ${teamAccentText};
}

.gs-scrim {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: ${inkAlpha(40)};
  animation-duration: var(--gs-motion-duration, ${String(MOTION.default)}ms);
  animation-timing-function: var(--gs-motion-easing, ${DEFAULT_EASING});
  animation-fill-mode: both;
}
.gs-scrim[data-state='open'] { animation-name: gs-fade-in; }
.gs-scrim[data-state='closed'] { animation-name: gs-fade-out; }
/*
 * A scrim that is closing, or closed and still mounted for a few frames after its
 * fade ends, is invisible but is still the top element: a tap on whatever is
 * under it (a court card) would land on it and be lost. Closing ends its input
 * at once. The important flag is there because the overlay carries an inline pointer-events: auto.
 */
.gs-scrim[data-state='closed'] { pointer-events: none !important; }

.gs-viewport {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${String(GUTTER_PX)}px;
  pointer-events: none;
}
.gs-viewport[data-presentation='sheet'] {
  align-items: flex-end;
  padding: 0;
}

.gs-surface {
  pointer-events: auto;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 85vh;
  max-height: 85dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${String(GUTTER_PX)}px;
  background: ${surfaceRaised};
  border: 1px solid ${rule};
  border-radius: ${String(RADIUS.surface)}px;
  box-shadow: 0 1px 2px ${inkAlpha(5)};
  font-size: 16px;
  line-height: 1.45;
  animation-duration: var(--gs-motion-duration, ${String(MOTION.default)}ms);
  animation-timing-function: var(--gs-motion-easing, ${DEFAULT_EASING});
  animation-fill-mode: both;
}
.gs-surface[data-state='closed'] { pointer-events: none !important; }
.gs-surface[data-size='sm'] { max-width: ${String(DIALOG_MAX_WIDTH_PX.sm)}px; }
.gs-surface[data-size='md'] { max-width: ${String(DIALOG_MAX_WIDTH_PX.md)}px; }
.gs-surface[data-size='lg'] { max-width: ${String(DIALOG_MAX_WIDTH_PX.lg)}px; }

.gs-surface[data-presentation='sheet'] {
  max-width: none;
  max-height: 90vh;
  max-height: 90dvh;
  border-radius: ${String(RADIUS.sheetTop)}px ${String(RADIUS.sheetTop)}px 0 0;
  border-bottom-width: 0;
  padding-bottom: max(${String(GUTTER_PX)}px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 24px ${inkAlpha(18)};
}

.gs-surface[data-motion='dialog'][data-state='open'] { animation-name: gs-dialog-in; }
.gs-surface[data-motion='dialog'][data-state='closed'] { animation-name: gs-dialog-out; }
.gs-surface[data-motion='sheet'][data-state='open'] { animation-name: gs-sheet-in; }
.gs-surface[data-motion='sheet'][data-state='closed'] { animation-name: gs-sheet-out; }
.gs-surface[data-motion='fade'][data-state='open'] { animation-name: gs-fade-in; }
.gs-surface[data-motion='fade'][data-state='closed'] { animation-name: gs-fade-out; }

.gs-sheet-handle {
  flex: none;
  width: 36px;
  height: 4px;
  margin: 0 auto;
  border-radius: ${String(RADIUS.pill)}px;
  background: ${rule};
}

.gs-surface__title {
  margin: 0;
  padding-inline-end: ${String(TOUCH_TARGET.minimum)}px;
  font-family: ${FONT.display};
  font-weight: 700;
  font-size: 21px;
  line-height: 1.15;
  letter-spacing: -0.01em;
}
.gs-surface__description {
  margin: 0;
  color: ${inkMuted};
}
.gs-surface__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.gs-surface__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}
.gs-surface[data-presentation='sheet'] .gs-surface__footer > .gs-button {
  flex: 1 1 auto;
}
.gs-surface__close {
  position: absolute;
  top: 6px;
  inset-inline-end: 6px;
  min-width: ${String(TOUCH_TARGET.minimum)}px;
  min-height: ${String(TOUCH_TARGET.minimum)}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: ${String(RADIUS.control)}px;
  background: transparent;
  color: ${inkMuted};
  font-size: 21px;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
}

.gs-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: ${String(TOUCH_TARGET.minimum)}px;
  min-width: ${String(TOUCH_TARGET.minimum)}px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: ${String(RADIUS.control)}px;
  background: transparent;
  color: ${ink};
  font-family: ${FONT.body};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  touch-action: manipulation;
}
.gs-button--primary {
  background: ${teamPrimary};
  color: var(--gs-on-fill, ${CHASSIS.surfaceRaised});
}
.gs-button--ghost {
  border-color: ${teamAccentText};
  color: ${teamAccentText};
}
.gs-button--danger {
  border-width: 1.5px;
  border-color: ${danger};
  color: ${danger};
}
.gs-button--quiet {
  color: ${inkMuted};
  text-decoration: underline;
  text-underline-offset: 3px;
}
.gs-button[disabled] {
  opacity: 0.5;
  cursor: default;
}

.gs-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.gs-field__label {
  font-family: ${FONT.mono};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${inkMuted};
}
.gs-input {
  box-sizing: border-box;
  width: 100%;
  min-height: ${String(TOUCH_TARGET.minimum)}px;
  padding: 10px 12px;
  border: 1px solid ${rule};
  border-radius: ${String(RADIUS.control)}px;
  background: ${surfaceRaised};
  color: ${ink};
  font-family: ${FONT.body};
  /* 16px is also what stops iOS Safari zooming the viewport on focus. */
  font-size: 16px;
}
.gs-input[inputmode='numeric'] {
  font-family: ${FONT.mono};
  font-variant-numeric: tabular-nums;
}

.gs-diagnostic {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${String(RADIUS.control)}px;
  background: ${surfaceSunken};
  font-family: ${FONT.mono};
  font-size: 12px;
  letter-spacing: 0.06em;
  color: ${inkMuted};
}
.gs-diagnostic__code {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${ink};
}

.gs-toast-region {
  position: fixed;
  z-index: 40;
  inset-inline: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: ${String(GUTTER_PX)}px;
  padding-bottom: max(${String(GUTTER_PX)}px, env(safe-area-inset-bottom));
  pointer-events: none;
}
.gs-toast {
  pointer-events: auto;
  box-sizing: border-box;
  width: 100%;
  max-width: ${String(DIALOG_MAX_WIDTH_PX.sm)}px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 8px 14px 16px;
  background: ${surfaceRaised};
  border: 1px solid ${rule};
  border-radius: ${String(RADIUS.surface)}px;
  box-shadow: 0 1px 2px ${inkAlpha(5)};
  font-size: 14px;
  line-height: 1.4;
  animation-name: gs-fade-in;
  animation-duration: var(--gs-motion-duration, ${String(MOTION.default)}ms);
  animation-timing-function: var(--gs-motion-easing, ${DEFAULT_EASING});
  animation-fill-mode: both;
}
.gs-toast[data-motion='dialog'] { animation-name: gs-dialog-in; }
.gs-toast__marker {
  flex: none;
  width: 6px;
  height: 6px;
  margin-top: 7px;
  border-radius: ${String(RADIUS.pill)}px;
  background: ${inkFaint};
}
.gs-toast[data-tone='success'] .gs-toast__marker { background: ${success}; }
.gs-toast__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.gs-toast__title { font-weight: 600; }
.gs-toast__body { color: ${inkMuted}; }
.gs-toast__dismiss {
  flex: none;
  min-width: ${String(TOUCH_TARGET.minimum)}px;
  min-height: ${String(TOUCH_TARGET.minimum)}px;
  padding: 0;
  border: 0;
  border-radius: ${String(RADIUS.control)}px;
  background: transparent;
  color: ${inkMuted};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
}

/*
 * PROVISIONAL. The handoff lists error states as NOT YET DESIGNED, so this is
 * deliberately the plain type-led block the voice rules imply and nothing more.
 * It is structure for a designer to replace, not a design.
 */
.gs-error {
  box-sizing: border-box;
  max-width: ${String(DIALOG_MAX_WIDTH_PX.md)}px;
  margin: 0 auto;
  padding: 28px ${String(GUTTER_PX)}px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: ${paper};
  font-size: 16px;
  line-height: 1.45;
}
.gs-error__title {
  margin: 0;
  font-family: ${FONT.display};
  font-weight: 700;
  font-size: 28px;
  line-height: 1.1;
  letter-spacing: -0.02em;
}
.gs-error__body {
  margin: 0;
  color: ${inkMuted};
}
.gs-error__rule {
  width: 100%;
  height: 0;
  margin: 0;
  border: 0;
  border-top: 1px solid ${rule};
}
.gs-error__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/*
 * The CSS half of prefers-reduced-motion. The components also compute it in
 * JavaScript (see motion.ts) so the two agree by construction: both collapse
 * to an opacity fade. This block is the guarantee, because it holds even for a
 * surface rendered without the hook.
 */
@media (prefers-reduced-motion: reduce) {
  .gs-scrim,
  .gs-surface,
  .gs-toast {
    animation-duration: ${String(MOTION.default)}ms;
    animation-timing-function: ${DEFAULT_EASING};
  }
  .gs-surface[data-state='open'],
  .gs-toast {
    animation-name: gs-fade-in;
  }
  .gs-surface[data-state='closed'] {
    animation-name: gs-fade-out;
  }
}
`;

const STYLE_ELEMENT_ID = 'good-season-ui-primitives';

/**
 * Install the stylesheet once per document. Idempotent, and a no-op with no
 * DOM, so it is safe to call from every primitive rather than relying on the
 * host to remember an import.
 */
export function ensurePrimitiveStyles(target?: Document): void {
  const doc = target ?? (typeof document === 'undefined' ? null : document);
  if (doc === null || doc.getElementById(STYLE_ELEMENT_ID) !== null) return;

  const element = doc.createElement('style');
  element.id = STYLE_ELEMENT_ID;
  element.textContent = PRIMITIVE_CSS;
  doc.head.appendChild(element);
}

export function usePrimitiveStyles(): void {
  // useInsertionEffect is the hook React reserves for injecting styles: it runs
  // before layout effects read geometry, so nothing measures an unstyled node.
  useInsertionEffect(() => {
    ensurePrimitiveStyles();
  }, []);
}
